/**
 * AffiliateTrainingCard — Fase 58.N (2026-08-30)
 *
 * Widget Training Gamificato dentro la dashboard affiliato (fuori AppShell).
 * • Al mount segnala la visita (missione `visit_affiliate_dashboard`)
 * • Checklist missioni del percorso affiliato + quiz (riusa QuizDialog)
 * • Banner obbligatorio finché non completato la prima volta
 * • Ascolta l'evento `gp:training-refresh` (es. dopo click su "Copia link")
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { QuizDialog, downloadCertificate } from '../pages/TrainingPage';
import { OpenAnswersDialog } from './training/OpenAnswersDialog';
import { toast } from 'sonner';
import {
  GraduationCap, CheckCircle2, Circle, Lock, Trophy, RotateCcw,
  BookOpenCheck, MousePointerClick, Sparkles, HelpCircle, FileDown, MessageSquareText,
} from 'lucide-react';

export const TRAINING_REFRESH_EVENT = 'gp:training-refresh';

const TYPE_META = {
  visit: { icon: MousePointerClick, labelKey: 'trainingPage.typeVisit', labelDefault: 'Esplora' },
  ui: { icon: Sparkles, labelKey: 'trainingPage.typeUi', labelDefault: 'Azione' },
  action: { icon: Sparkles, labelKey: 'trainingPage.typeAction', labelDefault: 'Azione reale' },
  quiz: { icon: HelpCircle, labelKey: 'trainingPage.typeQuiz', labelDefault: 'Quiz' },
  open: { icon: MessageSquareText, labelKey: 'trainingPage.typeOpen', labelDefault: 'Risposta libera' },
};

export function AffiliateTrainingCard() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [quizMission, setQuizMission] = useState(null);
  const [openMission, setOpenMission] = useState(null); // ★ Fase 59 — domande libere
  const [restarting, setRestarting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/training/me');
      setState(data);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => {
    // Traccia la visita (completa `visit_affiliate_dashboard`) poi carica lo stato
    (async () => {
      try {
        const { data } = await api.post('/training/track', { path: '/affiliate' });
        (data?.newly_completed || []).forEach((m) => {
          toast.success(t('trainingPage.missionCompleted', { defaultValue: 'Missione completata: {{title}}', title: m.title }), {
            description: t('trainingPage.pointsGained', { defaultValue: '+{{points}} punti', points: m.points }),
          });
        });
        if (data?.finished) {
          toast.success(t('trainingPage.trainingCompleteCert', 'Training completato! Hai ottenuto il Certificato HCF'));
        }
      } catch { /* non-blocking */ }
      refresh();
    })();
  }, [refresh, t]);

  useEffect(() => {
    const h = () => refresh();
    window.addEventListener(TRAINING_REFRESH_EVENT, h);
    return () => window.removeEventListener(TRAINING_REFRESH_EVENT, h);
  }, [refresh]);

  if (!state) return null;

  const progress = state.progress || { done: 0, total: 0, percent: 0 };
  const missions = (state.modules || []).flatMap((m) => m.missions || []);
  const completed = !!state.completed_at;
  const locked = !!state.locked;

  const restart = async () => {
    setRestarting(true);
    try {
      await api.post('/training/restart');
      toast.success(t('trainingPage.restartSuccess', 'Training riattivato: buon ripasso!'));
      refresh();
    } catch {
      toast.error(t('trainingPage.restartError', 'Errore riavvio training'));
    } finally { setRestarting(false); }
  };

  return (
    <Card
      className={locked ? 'border-[#E4C88A]' : ''}
      data-testid="affiliate-training-card"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <GraduationCap className="w-4 h-4 text-[hsl(var(--primary))]" />
            {t('trainingPage.affiliateTraining', 'Training Affiliato')}
            {locked && (
              <Badge className="bg-[#F3E7C7] text-[#6A4B00] hover:bg-[#F3E7C7] gap-1" data-testid="affiliate-training-mandatory-badge">
                <Lock className="w-3 h-3" /> {t('trainingPage.mandatoryBadge', 'Obbligatorio')}
              </Badge>
            )}
            {completed && (
              <Badge className="bg-[#D7EFE6] text-[#0C3C2D] hover:bg-[#D7EFE6] gap-1" data-testid="affiliate-training-completed-badge">
                <Trophy className="w-3 h-3" /> {t('trainingPage.hcfCertificate', 'Certificato HCF')}
              </Badge>
            )}
          </CardTitle>
          <div className="text-sm font-semibold tabular-nums" data-testid="affiliate-training-percent">
            {progress.percent}% · {progress.done}/{progress.total} {t('trainingPage.missionsLabel', 'missioni')}
          </div>
        </div>
        <Progress value={progress.percent} className="h-2 mt-2" />
        {locked && (
          <p className="text-xs text-[#6A4B00] mt-2">
            {t('trainingPage.affiliateMandatoryDesc', 'Completa queste missioni una sola volta per certificarti come Affiliato HCF: impari come funziona il tuo link, i tuoi lead e i tuoi compensi.')}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {missions.map((m) => {
          const meta = TYPE_META[m.type] || TYPE_META.visit;
          const TypeIcon = meta.icon;
          return (
            <div
              key={m.key}
              className={`flex items-start gap-3 rounded-lg border p-3 ${
                m.done ? 'border-[#BFE3D3] bg-[#EAF6F0]' : 'border-border bg-card'
              }`}
              data-testid={`affiliate-training-mission-${m.key}`}
            >
              {m.done ? (
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#1F7A5A]" data-testid={`affiliate-training-mission-${m.key}-done`} />
              ) : (
                <Circle className="w-5 h-5 mt-0.5 shrink-0 text-muted-foreground/50" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-semibold ${m.done ? 'text-[#0C3C2D]' : ''}`}>{m.title}</span>
                  <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0">
                    <TypeIcon className="w-3 h-3" />{t(meta.labelKey, meta.labelDefault)}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[#F3E7C7] border-[#E4D3A0] text-[#6A4B00]">
                    +{m.points} pt
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{m.desc}</p>
              </div>
              {!m.done && m.type === 'quiz' && (
                <Button size="sm" variant="secondary" className="shrink-0" onClick={() => setQuizMission(m)} data-testid={`affiliate-training-quiz-btn-${m.key}`}>
                  <BookOpenCheck className="w-4 h-4 mr-1" /> {t('trainingPage.typeQuiz', 'Quiz')}
                </Button>
              )}
              {!m.done && m.type === 'open' && (
                <Button size="sm" variant="secondary" className="shrink-0" onClick={() => setOpenMission(m)} data-testid={`affiliate-training-open-btn-${m.key}`}>
                  <MessageSquareText className="w-4 h-4 mr-1" /> {t('trainingPage.answerBtn', 'Rispondi')}
                </Button>
              )}
            </div>
          );
        })}

        {completed && (
          <div className="flex items-center justify-between gap-3 pt-1 flex-wrap">
            <p className="text-xs text-muted-foreground">
              {t('trainingPage.redoAnytime', 'Puoi rifare il training quando vuoi come ripasso: il certificato resta valido.')}
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => downloadCertificate()} data-testid="affiliate-training-certificate-btn">
                <FileDown className="w-3.5 h-3.5 mr-1" /> {t('trainingPage.downloadCert', 'Scarica attestato')}
              </Button>
              <Button size="sm" variant="outline" onClick={restart} disabled={restarting} data-testid="affiliate-training-restart-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> {t('trainingPage.redoTraining', 'Rifai il training')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <QuizDialog
        mission={quizMission}
        open={!!quizMission}
        onClose={() => setQuizMission(null)}
        onPassed={refresh}
      />

      {/* ★ Fase 59 — domande a risposta libera (obbligatorie per il certificato) */}
      <OpenAnswersDialog
        mission={openMission}
        open={!!openMission}
        onClose={() => setOpenMission(null)}
        onSaved={refresh}
      />
    </Card>
  );
}
